import { Text, type TextStyleOptions } from 'pixi.js';
import { INK, TRACKING, uiTextStyle } from './style';
import type { LabelSpec } from './text';
import { roundFont, scaledFont } from './metrics';

/**
 * **极简富文本** —— 给 `core/lore.ts` 产出的 tagged text 用。
 *
 * 目前**只支持颜色**：`LoreText` 的 `color` 档映射成 Pixi 的标签
 * （`<accent>…</accent>` 这种），再加一个段落小标题 `<h>`。
 * 不引入加粗/斜体/下划线等排版能力 —— 内容层用不到，多一种就多一块要维护的样式。
 *
 * 标签机制见 Pixi 的 `TextStyle.tagStyles` / `parseTaggedText`：
 * 未知标签会被当字面文本，所以这里的表就是唯一的“有效标签”清单。
 */
export function richTagStyles(scale: number): Record<string, TextStyleOptions> {
  return {
    // 段落小标题：比正文小、酸色、拉开字距 —— 印刷语言的“小标签”
    h: {
      fill: INK.accent,
      fontSize: roundFont(scaledFont(10, scale)),
      fontWeight: 'bold',
      letterSpacing: TRACKING.wide,
    },
    dim: { fill: INK.textDim },
    accent: { fill: INK.accent },
    info: { fill: INK.waiting },
    warn: { fill: INK.warning },
    danger: { fill: INK.danger },
    ok: { fill: INK.ok },
  };
}

/** 与 `text.ts` 的 `LabelSpec` 完全同形 —— 只是渲染时多带一套颜色标签样式 */
export type RichLabelSpec = LabelSpec;

/** 和 `label()` 同款，但带 `tagStyles`（能认颜色标签） */
export function richLabel(spec: RichLabelSpec): Text {
  const style = uiTextStyle({
    scale: spec.scale,
    size: spec.size,
    fill: spec.fill,
    role: spec.role,
    bold: spec.bold,
    tracking: spec.tracking,
    align: spec.align,
    wrapWidth: spec.wrapWidth,
  });
  style.tagStyles = richTagStyles(spec.scale);
  return new Text({ text: spec.text, style });
}
